import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faEdit,
  faTrash,
  faLock,
  faUnlock,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import { Search, Users, CalendarDays, Building2, School, Clock3 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

import { hasPermission } from "../../../utils/permissionUtils";
import { showConfirmDialog, showErrorToast } from "../../../components/ConfirmDialog";
import {
  formatDateVN,
  formatDateDDMMYYYY,
  splitVietnameseName,
} from "../../../utils/formatUtils";
import { fetchClassById, fetchClassStudents } from "../../../api/elearning/classes/classes";
import { exportClassStudentsToExcel } from "./exportClassStudents";
import { formatClassTimeDisplay } from "../utils/classTimeDisplay";

const formatGenderLabel = (raw) => {
  const g = String(raw || "").trim().toLowerCase();
  if (g === "male" || g === "nam") return "Nam";
  if (g === "female" || g === "nữ" || g === "nu") return "Nữ";
  return "—";
};

const ClassDetailModal = ({
  classroom,
  onClose,
  onEdit,
  onDelete,
  onToggleStatus,
}) => {
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [studentKeyword, setStudentKeyword] = useState("");
  const [classDetail, setClassDetail] = useState(classroom);

  const isActive =
    classDetail?.is_active === true || classDetail?.status === "active";

  const createdByLabel = useMemo(() => {
    const name = String(classDetail?.created_by_name || "").trim();
    if (name) return name;
    if (classDetail?.created_by) return "—";
    return "Hệ thống";
  }, [classDetail?.created_by_name, classDetail?.created_by]);

  useEffect(() => {
    setClassDetail(classroom);
  }, [classroom]);

  useEffect(() => {
    if (!classroom?.id) return;
    let cancelled = false;
    fetchClassById(classroom.id)
      .then((d) => {
        if (!cancelled && d) setClassDetail((prev) => ({ ...prev, ...d }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [classDetail?.id, classroom?.id]);

  /* ================= HANDLERS ================= */
  const handleDelete = async () => {
    if (classDetail?.id) {
      onDelete(classDetail);
    }
  };

  const handleToggleStatus = async () => {
    const confirm = await showConfirmDialog({
      title: isActive ? "Đóng lớp học" : "Mở lớp học",
      text: isActive
        ? `Bạn chắc chắn muốn đóng lớp học "${classDetail.name}" của giáo viên "${classDetail.teacher_name}"?`
        : `Bạn chắc chắn muốn mở lại lớp học "${classDetail.name}" của giáo viên "${classDetail.teacher_name}"?`,
      confirmText: isActive ? "Đóng" : "Mở",
    });

    if (confirm.isConfirmed) {
      onToggleStatus(classDetail.id, !isActive);
    }
  };

  useEffect(() => {
    if (!classroom?.id) return;
    let cancelled = false;
    const loadStudents = async () => {
      setLoadingStudents(true);
      try {
        const data = await fetchClassStudents(classDetail?.id || classroom.id);
        if (!cancelled) {
          setStudents(Array.isArray(data?.students) ? data.students : []);
        }
      } catch {
        if (!cancelled) setStudents([]);
      } finally {
        if (!cancelled) setLoadingStudents(false);
      }
    };
    loadStudents();
    return () => {
      cancelled = true;
    };
  }, [classDetail?.id, classroom?.id]);

  useEffect(() => {
    setStudentKeyword("");
  }, [classDetail?.id, classroom?.id]);

  const filteredStudents = useMemo(() => {
    const q = studentKeyword.trim().toLowerCase();
    const list = !q
      ? [...students]
      : students.filter((s) => {
          const name = String(s.full_name || "").toLowerCase();
          const phone = String(s.phone || "").toLowerCase();
          const studentCode = String(s.student_code || "").toLowerCase();
          return (
            name.includes(q) ||
            phone.includes(q) ||
            studentCode.includes(q)
          );
        });

    list.sort((a, b) => {
      const an = splitVietnameseName(a.full_name);
      const bn = splitVietnameseName(b.full_name);
      const byTen = an.ten.localeCompare(bn.ten, "vi", { sensitivity: "base" });
      if (byTen !== 0) return byTen;
      return an.ho.localeCompare(bn.ho, "vi", { sensitivity: "base" });
    });
    return list;
  }, [students, studentKeyword]);

  const handleDownloadStudents = () => {
    const result = exportClassStudentsToExcel(classDetail, students);
    if (!result.ok) showErrorToast(result.message);
  };

  if (!classroom) return null;

  /* ================= RENDER ================= */

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[999] bg-black/45 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col"
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ type: "spring", stiffness: 260, damping: 25 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 md:px-8 py-5 border-b border-gray-100 bg-gradient-to-r from-white via-red-50/30 to-white">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.18em] font-bold text-gray-400">
                  Chi tiết lớp học
                </p>
                <h2 className="text-2xl font-black text-gray-900 mt-1 truncate">
                  {classDetail.name || "—"}
                </h2>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <span className="px-2.5 py-1 rounded-full bg-red-50 text-red-700 text-[11px] font-bold">
                    #{classDetail.code || "N/A"}
                  </span>
                  <span className="px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-[11px] font-bold">
                    {classDetail.course_name || "Chưa gắn khóa học"}
                  </span>
                  <span
                    className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${
                      isActive ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {isActive ? "ĐANG HOẠT ĐỘNG" : "ĐÃ ĐÓNG"}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="h-10 w-10 rounded-xl border border-gray-200 bg-white text-gray-500 hover:text-gray-700 hover:bg-gray-50"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-6 bg-slate-50/30">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50 to-white p-3.5">
                <p className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Tổng học viên</p>
                <p className="text-xl font-black text-indigo-700 mt-1">{students.length}</p>
              </div>
              <div className="rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 to-white p-3.5">
                <p className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Giáo viên</p>
                <p className="text-sm font-bold text-blue-700 mt-1 truncate">
                  {classDetail.teacher_name || "Chưa có GV"}
                </p>
              </div>
              <div className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-white p-3.5">
                <p className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Chi nhánh</p>
                <p className="text-sm font-bold text-emerald-700 mt-1 truncate">
                  {classDetail.branch_name || "—"}
                </p>
              </div>
              <div className="rounded-2xl border border-amber-100 bg-gradient-to-br from-amber-50 to-white p-3.5">
                <p className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Loại lớp</p>
                <p className="text-sm font-bold text-amber-700 mt-1">
                  {classDetail.class_type === "school" ? "Tại trường học" : "Tại trung tâm"}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-blue-100 bg-white p-4 space-y-3 shadow-sm">
                <p className="text-xs font-black uppercase tracking-widest text-gray-400">Thông tin lớp</p>
                <InfoRow
                  icon={<Building2 size={15} className="text-emerald-600" />}
                  iconBgClass="bg-emerald-50 border-emerald-100"
                  label="Phòng học"
                  value={classDetail.room_name || "—"}
                />
                <InfoRow
                  icon={<School size={15} className="text-indigo-600" />}
                  iconBgClass="bg-indigo-50 border-indigo-100"
                  label="Trường học"
                  value={
                    classDetail.class_type === "school"
                      ? classDetail.school_name || "Chưa chọn trường học"
                      : "Không áp dụng"
                  }
                />
                <InfoRow
                  icon={<Clock3 size={15} className="text-amber-600" />}
                  iconBgClass="bg-amber-50 border-amber-100"
                  label="Thời gian học"
                  value={formatClassTimeDisplay(classDetail.class_time)}
                />
                <InfoRow
                  icon={<CalendarDays size={15} className="text-rose-600" />}
                  iconBgClass="bg-rose-50 border-rose-100"
                  label="Thời gian khóa"
                  value={`${classDetail.start_date ? formatDateVN(classDetail.start_date) : "—"} → ${
                    classDetail.end_date ? formatDateVN(classDetail.end_date) : "—"
                  }`}
                />
              </div>

              <div className="rounded-2xl border border-violet-100 bg-white p-4 space-y-3 shadow-sm">
                <p className="text-xs font-black uppercase tracking-widest text-gray-400">Thông tin hệ thống</p>
                <InfoRow label="Người tạo" value={createdByLabel} />
                <InfoRow
                  label="Cập nhật cuối"
                  value={
                    classDetail.last_changed
                      ? new Date(classDetail.last_changed).toLocaleDateString("vi-VN")
                      : "—"
                  }
                />
                <InfoRow label="Mã lớp" value={classDetail.code || "—"} />
                <InfoRow label="Khóa học" value={classDetail.course_name || "—"} />
              </div>
            </div>

            <div className="rounded-2xl border border-gray-100 bg-white overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <p className="text-sm font-black text-gray-800 flex items-center gap-2">
                  <Users size={15} className="text-red-600" />
                  Danh sách học viên ({filteredStudents.length}/{students.length})
                </p>
                <div className="relative w-full sm:w-72">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    value={studentKeyword}
                    onChange={(e) => setStudentKeyword(e.target.value)}
                    placeholder="Tìm theo mã HS, tên, SĐT..."
                    className="w-full pl-9 pr-3 py-2 rounded-xl border border-gray-200 text-sm outline-none focus:border-red-400"
                  />
                </div>
              </div>
              <div className="max-h-80 overflow-auto">
                {loadingStudents ? (
                  <p className="px-4 py-4 text-sm text-gray-400">Đang tải danh sách học viên...</p>
                ) : filteredStudents.length === 0 ? (
                  <p className="px-4 py-4 text-sm text-gray-400 italic">
                    {students.length === 0
                      ? "Chưa có học viên trong lớp này."
                      : "Không có học viên khớp từ khóa."}
                  </p>
                ) : (
                  <table className="w-full text-sm min-w-[1180px]">
                    <thead className="bg-gray-50 text-gray-500 sticky top-0">
                      <tr>
                        <th className="text-left px-4 py-2 font-semibold">Mã học sinh</th>
                        <th className="text-left px-4 py-2 font-semibold">Họ</th>
                        <th className="text-left px-4 py-2 font-semibold">Tên</th>
                        <th className="text-left px-4 py-2 font-semibold">Ngày sinh</th>
                        <th className="text-left px-4 py-2 font-semibold">Giới tính</th>
                        <th className="text-left px-4 py-2 font-semibold">SĐT</th>
                        <th className="text-left px-4 py-2 font-semibold">Email</th>
                        <th className="text-left px-4 py-2 font-semibold">Địa chỉ</th>
                        <th className="text-left px-4 py-2 font-semibold">Tên ba/mẹ</th>
                        <th className="text-left px-4 py-2 font-semibold">SĐT ba/mẹ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredStudents.map((s) => {
                        const { ho, ten } = splitVietnameseName(s.full_name);
                        const displayTen = ten || "—";
                        return (
                        <tr key={s.student_id} className="border-t border-gray-50 hover:bg-red-50/30">
                          <td className="px-4 py-2 text-gray-700 font-semibold">{s.student_code || "—"}</td>
                          <td className="px-4 py-2 text-gray-800">{ho || "—"}</td>
                          <td className="px-4 py-2 text-gray-800 font-medium">
                            <div className="flex items-center gap-2">
                              <span className="h-6 w-6 rounded-full bg-red-100 text-red-700 text-[10px] font-bold flex items-center justify-center shrink-0">
                                {displayTen.charAt(0).toUpperCase()}
                              </span>
                              <span>{displayTen}</span>
                            </div>
                          </td>
                          <td className="px-4 py-2 text-gray-600">
                            {s.dob ? formatDateDDMMYYYY(s.dob) : "—"}
                          </td>
                          <td className="px-4 py-2 text-gray-600">{formatGenderLabel(s.gender)}</td>
                          <td className="px-4 py-2 text-gray-600">{s.phone || "—"}</td>
                          <td className="px-4 py-2 text-gray-600">{s.email || "—"}</td>
                          <td className="px-4 py-2 text-gray-600">{s.address || "—"}</td>
                          <td className="px-4 py-2 text-gray-600">{s.parent_name || "—"}</td>
                          <td className="px-4 py-2 text-gray-600">{s.parent_phone || "—"}</td>
                        </tr>
                      );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>

          <div className="px-6 md:px-8 py-4 bg-white border-t border-gray-100 flex flex-wrap items-center justify-between gap-2">
            <button
              onClick={handleDownloadStudents}
              className="px-3 py-2 rounded-xl border border-indigo-200 bg-indigo-50 text-indigo-700 flex items-center gap-2 hover:bg-indigo-100 font-semibold text-sm"
            >
              <FontAwesomeIcon icon={faDownload} />
              Tải DS học viên
            </button>
            <div className="flex flex-wrap items-center justify-end gap-2 ml-auto">
              {hasPermission("elearning.classes", "U") && (
                <button
                  onClick={() => onEdit(classroom)}
                  className="px-3 py-2 rounded-xl border border-gray-200 flex items-center gap-2 hover:bg-gray-50 text-gray-700 font-semibold text-sm"
                >
                  <FontAwesomeIcon icon={faEdit} />
                  Sửa
                </button>
              )}

              {hasPermission("elearning.classes", "U") && (
                <button
                  onClick={handleToggleStatus}
                  className={`px-3 py-2 rounded-xl border flex items-center gap-2 font-semibold text-sm ${
                    isActive
                      ? "text-orange-600 border-orange-200 hover:bg-orange-50"
                      : "text-green-600 border-green-200 hover:bg-green-50"
                  }`}
                >
                  <FontAwesomeIcon icon={isActive ? faLock : faUnlock} />
                  {isActive ? "Đóng" : "Mở lại"}
                </button>
              )}

              {hasPermission("elearning.classes", "D") && (
                <button
                  onClick={handleDelete}
                  className="px-3 py-2 rounded-xl border border-red-200 text-red-600 hover:bg-red-50 flex items-center gap-2 font-semibold text-sm"
                >
                  <FontAwesomeIcon icon={faTrash} />
                  Xóa
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

function InfoRow({ icon, label, value, iconBgClass }) {
  return (
    <div className="flex items-start gap-3">
      <div
        className={`h-7 w-7 rounded-lg border text-gray-500 flex items-center justify-center mt-0.5 ${
          iconBgClass || "bg-gray-50 border-gray-100"
        }`}
      >
        {icon || <span className="text-xs">•</span>}
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-widest font-bold text-gray-400">{label}</p>
        <p className="text-sm font-semibold text-gray-800">
          {value || "—"}
        </p>
      </div>
    </div>
  );
}

export default ClassDetailModal;
